import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import timetableService from '../services/timetableService';
import staffService from '../services/staffService';
import classesService from '../services/classesService';
import gradesService from '../services/gradesService';
import schoolsService from '../services/schoolsService';
import facilitiesService from '../services/facilitiesService';
import {
  Plus, X, CalendarBlank, Clock, Table, Users,
  Student, Chalkboard, CaretLeft, CaretRight, Download, CircleNotch
} from '@phosphor-icons/react';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday'
};

const PERIOD_TYPE_COLORS = {
  lesson: '#064E3B',
  break: '#D97706',
  assembly: '#3B82F6',
  lunch: '#9333EA'
};

function TimetablePage() {
  const { user } = useAuth();
  const [view, setView] = useState('class'); // class, teacher, periods
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [facilityRooms, setFacilityRooms] = useState([]);
  const [currentTermId, setCurrentTermId] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [timetable, setTimetable] = useState(null);
  const [teacherTimetable, setTeacherTimetable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Modals
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [editingPeriodId, setEditingPeriodId] = useState(null);
  const [confirmDeletePeriodId, setConfirmDeletePeriodId] = useState(null);
  const [confirmDeleteEntryId, setConfirmDeleteEntryId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  
  // Forms
  const [entryForm, setEntryForm] = useState({
    class_id: '',
    subject_id: '',
    teacher_id: '',
    period_id: '',
    day_of_week: 'monday',
    academic_term_id: '',
    room: '',
    facility_room_id: ''
  });
  
  const [periodForm, setPeriodForm] = useState({
    name: '',
    period_number: 1,
    start_time: '08:00',
    end_time: '08:40',
    period_type: 'lesson'
  });

  // Load initial data
  useEffect(() => {
    Promise.all([
      classesService.listClasses(),
      staffService.listTeachers(),
      gradesService.listSubjects(),
      timetableService.listPeriods(),
      facilitiesService.rooms().catch(() => [])
    ]).then(([classData, teacherData, subjectData, periodData, roomData]) => {
      setClasses(classData || []);
      // Handle both array and object with items property
      const teachersList = Array.isArray(teacherData) ? teacherData : (teacherData?.items || []);
      setTeachers(teachersList);
      setSubjects(subjectData || []);
      setPeriods(periodData || []);
      setFacilityRooms(roomData || []);
    }).catch(err => {
      console.error('Error loading initial data:', err);
      toast.error('Failed to load timetable data');
    });
  }, []);

  // Load the school's current academic term (used as the default for viewing
  // and creating timetable entries)
  useEffect(() => {
    if (!user?.school_id) return;
    schoolsService.listTerms(user.school_id)
      .then(data => {
        const terms = data || [];
        const current = terms.find(t => t.is_current);
        setCurrentTermId(current ? current.id : (terms[0] ? terms[0].id : ''));
      })
      .catch(() => {});
  }, [user]);

  // Load class timetable
  const loadClassTimetable = useCallback(() => {
    if (!selectedClass || !currentTermId) return;
    setLoading(true);
    timetableService.getClassTimetable(selectedClass, currentTermId)
      .then(data => setTimetable(data))
      .catch(err => toast.error('Failed to load timetable'))
      .finally(() => setLoading(false));
  }, [selectedClass, currentTermId]);

  useEffect(() => {
    if (selectedClass && view === 'class') {
      loadClassTimetable();
    }
  }, [selectedClass, view, loadClassTimetable]);

  // Load teacher timetable
  const loadTeacherTimetable = useCallback(() => {
    if (!selectedTeacher || !currentTermId) return;
    setLoading(true);
    timetableService.getTeacherTimetable(selectedTeacher, currentTermId)
      .then(data => setTeacherTimetable(data))
      .catch(err => toast.error('Failed to load timetable'))
      .finally(() => setLoading(false));
  }, [selectedTeacher, currentTermId]);

  useEffect(() => {
    if (selectedTeacher && view === 'teacher') {
      loadTeacherTimetable();
    }
  }, [selectedTeacher, view, loadTeacherTimetable]);

  // Seed default periods
  const seedPeriods = async () => {
    try {
      const result = await timetableService.seedDefaultPeriods();
      toast.success(result.message);
      const data = await timetableService.listPeriods();
      setPeriods(data || []);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openCreatePeriodModal = () => {
    setEditingPeriodId(null);
    setPeriodForm({
      name: '',
      period_number: 1,
      start_time: '08:00',
      end_time: '08:40',
      period_type: 'lesson'
    });
    setShowPeriodModal(true);
  };

  const openEditPeriodModal = (period) => {
    setEditingPeriodId(period.id);
    setPeriodForm({
      name: period.name,
      period_number: period.period_number,
      start_time: period.start_time,
      end_time: period.end_time,
      period_type: period.period_type
    });
    setShowPeriodModal(true);
  };

  const handleSavePeriod = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingPeriodId) {
        await timetableService.updatePeriod(editingPeriodId, periodForm);
        toast.success('Period updated!');
      } else {
        await timetableService.createPeriod(periodForm);
        toast.success('Period created!');
      }

      setShowPeriodModal(false);
      setEditingPeriodId(null);
      const data = await timetableService.listPeriods();
      setPeriods(data || []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePeriod = (periodId) => {
    setConfirmDeletePeriodId(periodId);
  };

  const confirmDeletePeriod = async () => {
    if (!confirmDeletePeriodId) return;

    setConfirmLoading(true);
    try {
      await timetableService.deletePeriod(confirmDeletePeriodId);
      toast.success('Period deleted');
      const data = await timetableService.listPeriods();
      setPeriods(data || []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setConfirmLoading(false);
      setConfirmDeletePeriodId(null);
    }
  };

  // Download class timetable PDF
  const downloadClassTimetable = async () => {
    if (!selectedClass) {
      toast.error('Please select a class first');
      return;
    }
    try {
      const response = await timetableService.exportClassTimetablePdf(selectedClass, 'term_1_2026');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `class_timetable_${selectedClass}_term_1_2026.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Timetable downloaded successfully');
    } catch (error) {
      toast.error('Failed to download timetable');
      console.error(error);
    }
  };

  // Download teacher timetable PDF
  const downloadTeacherTimetable = async () => {
    if (!selectedTeacher) {
      toast.error('Please select a teacher first');
      return;
    }
    try {
      const response = await timetableService.exportTeacherTimetablePdf(selectedTeacher, 'term_1_2026');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `teacher_timetable_${selectedTeacher}_term_1_2026.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Timetable downloaded successfully');
    } catch (error) {
      toast.error('Failed to download timetable');
      console.error(error);
    }
  };

  // Create timetable entry
  const handleCreateEntry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Empty-string facility_room_id would otherwise be sent as a literal
      // (invalid) id instead of "no room selected" — normalize to null.
      await timetableService.createEntry({ ...entryForm, facility_room_id: entryForm.facility_room_id || null });
      toast.success('Timetable entry created!');
      setShowEntryModal(false);
      if (view === 'class') loadClassTimetable();
      if (view === 'teacher') loadTeacherTimetable();
    } catch (error) {
      toast.error(error.message || 'Failed to create entry — check for teacher/room conflicts');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete entry
  const handleDeleteEntry = (entryId) => {
    setConfirmDeleteEntryId(entryId);
  };

  const confirmDeleteEntry = async () => {
    if (!confirmDeleteEntryId) return;

    setConfirmLoading(true);
    try {
      await timetableService.deleteEntry(confirmDeleteEntryId);
      toast.success('Entry deleted');
      if (view === 'class') loadClassTimetable();
      if (view === 'teacher') loadTeacherTimetable();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setConfirmLoading(false);
      setConfirmDeleteEntryId(null);
    }
  };

  // Get entry for a specific day and period
  const getEntryForSlot = (schedule, day, periodId) => {
    if (!schedule || !schedule[day]) return null;
    return schedule[day].find(e => e.period_id === periodId);
  };

  // Open entry modal with prefilled data
  const openEntryModal = (day, periodId) => {
    setEntryForm({
      ...entryForm,
      class_id: view === 'teacher' ? entryForm.class_id : (selectedClass || ''),
      teacher_id: view === 'teacher' ? selectedTeacher : (entryForm.teacher_id || ''),
      period_id: periodId,
      day_of_week: day,
      academic_term_id: currentTermId
    });
    setShowEntryModal(true);
  };

  return (
    <Layout>
      <div className="page" data-testid="timetable-page">
        <div className="page-header">
          <div>
            <h1>Timetable</h1>
            <p>Manage class schedules and period assignments</p>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-secondary" 
              onClick={openCreatePeriodModal}
              data-testid="add-period-btn"
            >
              <Clock size={18} /> Add Period
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                setEntryForm({
                  ...entryForm,
                  class_id: view === 'teacher' ? entryForm.class_id : (selectedClass || ''),
                  teacher_id: view === 'teacher' ? selectedTeacher : (entryForm.teacher_id || ''),
                  academic_term_id: currentTermId
                });
                setShowEntryModal(true);
              }}
              disabled={(view === 'class' && !selectedClass) || (view === 'teacher' && !selectedTeacher)}
              data-testid="add-entry-btn"
            >
              <Plus size={18} /> Add Entry
            </button>
          </div>
        </div>

        {/* View Tabs */}
        <div className="tabs-container">
          <button 
            className={`tab-btn ${view === 'class' ? 'active' : ''}`}
            onClick={() => setView('class')}
          >
            <Chalkboard size={18} /> Class Timetable
          </button>
          <button 
            className={`tab-btn ${view === 'teacher' ? 'active' : ''}`}
            onClick={() => setView('teacher')}
          >
            <Users size={18} /> Teacher Schedule
          </button>
          <button 
            className={`tab-btn ${view === 'periods' ? 'active' : ''}`}
            onClick={() => setView('periods')}
          >
            <Clock size={18} /> Period Setup
          </button>
        </div>

        {/* Class Timetable View */}
        {view === 'class' && (
          <div className="timetable-content">
            <div className="filters-row">
              <div className="form-group">
                <label>Select Class</label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  data-testid="class-select"
                >
                  <option value="">Choose a class...</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading timetable...</div>
            ) : timetable ? (
              <div className="timetable-grid-container">
                <div className="timetable-header">
                  <h3>{timetable.class_name} - Weekly Schedule</h3>
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={downloadClassTimetable}
                    title="Download PDF"
                  >
                    <Download size={16} /> Export PDF
                  </button>
                </div>
                <div className="timetable-grid" data-testid="timetable-grid">
                  {/* Header Row */}
                  <div className="timetable-cell header time-col">Time</div>
                  {DAYS.map(day => (
                    <div key={day} className="timetable-cell header">{DAY_LABELS[day]}</div>
                  ))}
                  
                  {/* Period Rows */}
                  {timetable.periods.map(period => (
                    <React.Fragment key={period.id}>
                      <div className="timetable-cell time-col">
                        <div className="period-time">
                          <span className="period-name">{period.name}</span>
                          <span className="time-range">{period.start_time} - {period.end_time}</span>
                        </div>
                      </div>
                      {DAYS.map(day => {
                        const entry = getEntryForSlot(timetable.schedule, day, period.id);
                        const isBreak = period.period_type !== 'lesson';
                        
                        return (
                          <div 
                            key={`${day}-${period.id}`} 
                            className={`timetable-cell ${isBreak ? 'break-cell' : ''} ${entry ? 'has-entry' : 'empty-cell'}`}
                            onClick={() => !isBreak && !entry && openEntryModal(day, period.id)}
                          >
                            {isBreak ? (
                              <span className="break-label">{period.name}</span>
                            ) : entry ? (
                              <div className="entry-content" onClick={(e) => e.stopPropagation()}>
                                <span className="subject-name">{entry.subject_name}</span>
                                <span className="teacher-name">{entry.teacher_name}</span>
                                {entry.room && <span className="room">Room {entry.room}</span>}
                                <button 
                                  className="delete-entry-btn"
                                  onClick={() => handleDeleteEntry(entry.id)}
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            ) : (
                              <span className="add-hint">+ Add</span>
                            )}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-state-card">
                <CalendarBlank size={48} weight="duotone" />
                <h3>Select a Class</h3>
                <p>Choose a class to view or edit its timetable</p>
              </div>
            )}
          </div>
        )}

        {/* Teacher Timetable View */}
        {view === 'teacher' && (
          <div className="timetable-content">
            <div className="filters-row">
              <div className="form-group">
                <label>Select Teacher</label>
                <select
                  value={selectedTeacher}
                  onChange={(e) => setSelectedTeacher(e.target.value)}
                  data-testid="teacher-select"
                >
                  <option value="">Choose a teacher...</option>
                  {teachers.map(teacher => (
                    <option key={teacher.id} value={teacher.id}>{teacher.full_name}</option>
                  ))}
                </select>
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading timetable...</div>
            ) : teacherTimetable ? (
              <div className="timetable-grid-container">
                <div className="timetable-header">
                  <h3>{teacherTimetable.teacher_name}'s Schedule</h3>
                  <div className="header-actions">
                    <span className="periods-count">{teacherTimetable.total_periods_per_week} periods/week</span>
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={downloadTeacherTimetable}
                      title="Download PDF"
                    >
                      <Download size={16} /> Export PDF
                    </button>
                  </div>
                </div>
                <div className="timetable-grid" data-testid="teacher-timetable-grid">
                  {/* Header Row */}
                  <div className="timetable-cell header time-col">Time</div>
                  {DAYS.map(day => (
                    <div key={day} className="timetable-cell header">{DAY_LABELS[day]}</div>
                  ))}
                  
                  {/* Period Rows */}
                  {teacherTimetable.periods.map(period => (
                    <React.Fragment key={period.id}>
                      <div className="timetable-cell time-col">
                        <div className="period-time">
                          <span className="period-name">{period.name}</span>
                          <span className="time-range">{period.start_time} - {period.end_time}</span>
                        </div>
                      </div>
                      {DAYS.map(day => {
                        const entry = getEntryForSlot(teacherTimetable.schedule, day, period.id);
                        const isBreak = period.period_type !== 'lesson';
                        
                        return (
                          <div 
                            key={`${day}-${period.id}`} 
                            className={`timetable-cell ${isBreak ? 'break-cell' : ''} ${entry ? 'has-entry' : 'empty-cell'}`}
                            onClick={() => !isBreak && !entry && openEntryModal(day, period.id)}
                          >
                            {isBreak ? (
                              <span className="break-label">{period.name}</span>
                            ) : entry ? (
                              <div className="entry-content teacher-view" onClick={(e) => e.stopPropagation()}>
                                <span className="subject-name">{entry.subject_name}</span>
                                <span className="class-name">{entry.class_name}</span>
                                {entry.room && <span className="room">Room {entry.room}</span>}
                                <button 
                                  className="delete-entry-btn"
                                  onClick={() => handleDeleteEntry(entry.id)}
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            ) : (
                              <span className="add-hint">+ Add</span>
                            )}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-state-card">
                <Users size={48} weight="duotone" />
                <h3>Select a Teacher</h3>
                <p>Choose a teacher to view their weekly schedule</p>
              </div>
            )}
          </div>
        )}

        {/* Periods Setup View */}
        {view === 'periods' && (
          <div className="periods-content">
            {periods.length === 0 ? (
              <div className="empty-state-card">
                <Clock size={48} weight="duotone" />
                <h3>No Periods Defined</h3>
                <p>Set up school periods to create timetables</p>
                <div className="empty-actions">
                  <button className="btn btn-primary" onClick={seedPeriods}>
                    Load Default Ghana School Periods
                  </button>
                  <button className="btn btn-secondary" onClick={openCreatePeriodModal}>
                    Add Custom Period
                  </button>
                </div>
              </div>
            ) : (
              <div className="periods-list">
                <div className="section-header">
                  <h3>School Periods</h3>
                  <button className="btn btn-sm btn-primary" onClick={openCreatePeriodModal}>
                    <Plus size={16} /> Add Period
                  </button>
                </div>
                <div className="periods-grid">
                  {periods.map(period => (
                    <div 
                      key={period.id} 
                      className="period-card"
                      style={{ borderLeftColor: PERIOD_TYPE_COLORS[period.period_type] }}
                      data-testid={`period-${period.id}`}
                    >
                      <div className="period-card-actions">
                        <button className="btn btn-icon btn-sm" onClick={() => openEditPeriodModal(period)}>
                          Edit
                        </button>
                        <button className="btn btn-icon btn-sm btn-danger" onClick={() => handleDeletePeriod(period.id)}>
                          Delete
                        </button>
                      </div>
                      <div className="period-number">#{period.period_number}</div>
                      <div className="period-info">
                        <span className="period-name">{period.name}</span>
                        <span className="period-time">{period.start_time} - {period.end_time}</span>
                      </div>
                      <span 
                        className="period-type-badge"
                        style={{ backgroundColor: PERIOD_TYPE_COLORS[period.period_type] }}
                      >
                        {period.period_type}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Entry Modal */}
        {showEntryModal && (
          <div className="modal-overlay" onClick={() => setShowEntryModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} data-testid="entry-modal">
              <div className="modal-header">
                <h2>Add Timetable Entry</h2>
                <button className="modal-close" onClick={() => setShowEntryModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleCreateEntry} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Class</label>
                    <select
                      value={entryForm.class_id}
                      onChange={(e) => setEntryForm({...entryForm, class_id: e.target.value})}
                      required
                      disabled={submitting}
                    >
                      <option value="">Select class...</option>
                      {classes.map(cls => (
                        <option key={cls.id} value={cls.id}>{cls.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Day</label>
                    <select
                      value={entryForm.day_of_week}
                      onChange={(e) => setEntryForm({...entryForm, day_of_week: e.target.value})}
                      required
                      disabled={submitting}
                    >
                      {DAYS.map(day => (
                        <option key={day} value={day}>{DAY_LABELS[day]}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Period</label>
                    <select
                      value={entryForm.period_id}
                      onChange={(e) => setEntryForm({...entryForm, period_id: e.target.value})}
                      required
                      disabled={submitting}
                    >
                      <option value="">Select period...</option>
                      {periods.filter(p => p.period_type === 'lesson').map(period => (
                        <option key={period.id} value={period.id}>
                          {`${period.name} (${period.start_time} - ${period.end_time})`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Subject</label>
                    <select
                      value={entryForm.subject_id}
                      onChange={(e) => setEntryForm({...entryForm, subject_id: e.target.value})}
                      required
                      disabled={submitting}
                    >
                      <option value="">Select subject...</option>
                      {subjects.map(subj => (
                        <option key={subj.id} value={subj.id}>{subj.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Teacher</label>
                    <select
                      value={entryForm.teacher_id}
                      onChange={(e) => setEntryForm({...entryForm, teacher_id: e.target.value})}
                      required
                      disabled={submitting}
                    >
                      <option value="">Select teacher...</option>
                      {teachers.map(teacher => (
                        <option key={teacher.id} value={teacher.id}>{teacher.full_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Room (Optional)</label>
                    <input
                      type="text"
                      value={entryForm.room}
                      onChange={(e) => setEntryForm({...entryForm, room: e.target.value})}
                      placeholder="e.g., Room 101"
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Bookable Room (Optional)</label>
                    <select
                      value={entryForm.facility_room_id}
                      onChange={(e) => setEntryForm({...entryForm, facility_room_id: e.target.value})}
                      disabled={submitting}
                    >
                      <option value="">No bookable room linked</option>
                      {facilityRooms.map(room => (
                        <option key={room.id} value={room.id}>{room.name}{room.code ? ` (${room.code})` : ''}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEntryModal(false)} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="save-entry-btn">
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Saving...
                      </span>
                    ) : 'Add Entry'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add/Edit Period Modal */}
        {showPeriodModal && (
          <div className="modal-overlay" onClick={() => { setShowPeriodModal(false); setEditingPeriodId(null); }}>
            <div className="modal" onClick={e => e.stopPropagation()} data-testid="period-modal">
              <div className="modal-header">
                <h2>{editingPeriodId ? 'Edit Period' : 'Add Period'}</h2>
                <button className="modal-close" onClick={() => { setShowPeriodModal(false); setEditingPeriodId(null); }}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleSavePeriod} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Period Name</label>
                    <input
                      type="text"
                      value={periodForm.name}
                      onChange={(e) => setPeriodForm({...periodForm, name: e.target.value})}
                      placeholder="e.g., Period 1"
                      required
                      disabled={submitting}
                    />
                  </div>
                  <div className="form-group">
                    <label>Period Number</label>
                    <input
                      type="number"
                      value={periodForm.period_number}
                      onChange={(e) => setPeriodForm({...periodForm, period_number: parseInt(e.target.value)})}
                      min="0"
                      required
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Time</label>
                    <input
                      type="time"
                      value={periodForm.start_time}
                      onChange={(e) => setPeriodForm({...periodForm, start_time: e.target.value})}
                      required
                      disabled={submitting}
                    />
                  </div>
                  <div className="form-group">
                    <label>End Time</label>
                    <input
                      type="time"
                      value={periodForm.end_time}
                      onChange={(e) => setPeriodForm({...periodForm, end_time: e.target.value})}
                      required
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Period Type</label>
                  <select
                    value={periodForm.period_type}
                    onChange={(e) => setPeriodForm({...periodForm, period_type: e.target.value})}
                    disabled={submitting}
                  >
                    <option value="lesson">Lesson</option>
                    <option value="break">Break</option>
                    <option value="assembly">Assembly</option>
                    <option value="lunch">Lunch</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowPeriodModal(false); setEditingPeriodId(null); }} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="save-period-btn">
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Saving...
                      </span>
                    ) : (editingPeriodId ? 'Save Changes' : 'Add Period')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      <DialogModal
        isOpen={Boolean(confirmDeletePeriodId)}
        onClose={() => setConfirmDeletePeriodId(null)}
        title="Delete Period"
        message="Are you sure you want to delete this period? This action cannot be undone."
        type="warning"
        primaryAction={{ label: confirmLoading ? 'Deleting...' : 'Delete', onClick: confirmDeletePeriod }}
        secondaryAction={{ label: 'Cancel' }}
        showCloseButton={!confirmLoading}
        isLoading={confirmLoading}
        disableCloseWhenLoading={true}
      />
      <DialogModal
        isOpen={Boolean(confirmDeleteEntryId)}
        onClose={() => setConfirmDeleteEntryId(null)}
        title="Delete Timetable Entry"
        message="Are you sure you want to delete this timetable entry?"
        type="warning"
        primaryAction={{ label: confirmLoading ? 'Deleting...' : 'Delete', onClick: confirmDeleteEntry }}
        secondaryAction={{ label: 'Cancel' }}
        showCloseButton={!confirmLoading}
        isLoading={confirmLoading}
        disableCloseWhenLoading={true}
      />
      </div>
    </Layout>
  );
}

export default TimetablePage;
